import React, { useState } from "react";
import { Col, Row, Card, ListGroupItem, Badge, CardTitle } from "reactstrap";
import { format } from "date-fns";
import Chart from "react-google-charts";
import { Info } from "react-feather";

import SelectInput from "../../common/components/Select";
import Loader from "../../common/components/Loader";
import Error from "../../common/components/ErrorOverlay";
import { divergingScheme4 as colors, getColor } from "../../common/helpers/colors";
import { useTypedSelector } from "../../store/models/root.interface";
import { ChartItem, EventsCharts } from "../../api";
import { SelectOnChangeEvent } from "../../common/types/Select.types";
import { useMemo } from "react";

const options: { label: string, value: keyof EventsCharts }[] = [
  {
    label: "Recent Attendance",
    value: "recentAttendance",
  },
  {
    label: "Highest Attendance",
    value: "highestAttendance",
  },
  {
    label: "Recent Scores",
    value: "recentScores",
  },
  {
    label: "Highest Scores",
    value: "highestScores",
  },
  {
    label: "Attendance by Type",
    value: "attendanceByType",
  },
];

const barChartOptions = {
  fontName: "Helvetica",
  backgroundColor: "transparent",
  legend: { position: "none" },
  chartArea: { height: "81%", width: "80%" },
  seriesType: "bars",
  vAxis: {
    minValue: 0,
    maxValue: 100,
    textStyle: {color: "d3d3d3"},
    gridlines: {color: "d3d3d3"},
    baselineColor: "d3d3d3"
  },
  textPosition: "none",
  bar: { groupWidth: "30%" },
  animation: {
    startup: true,
    duration: 100,
    easing: "linear",
  },
};

const baseComboBarChartOptions = {
  fontName: "Helvetica",
  backgroundColor: "transparent",
  color: "white",
  seriesType: "bars",
  chartArea: { height: "81%", width: "80%" },
  legend: {
    position: "bottom",
    textStyle: { color: "d3d3d3"}
  },
  vAxis: {
    textStyle: {color: "d3d3d3"},
    gridlines: {color: "d3d3d3"},
  },
  animation: {
    startup: true,
    duration: 100,
    easing: "linear",
  },
};

const basePieChartOptions = {
  fontName: "Helvetica",
  backgroundColor: "transparent",
  color:"white",
  //legend: { position: "labeled", labels:{fontColor:"white"} },
  legend: {
    position: "labeled",
    textStyle: {color: "d3d3d3"}
  },
  pieSliceText: "percentage",
  pieSliceTextStyle: {
    fontSize: 8,
    color: "white"
  },
  is3D: false,
  chartArea: {
    left: 0,
    top: "2px",
    width: "100%",
    height: "85%",
  },
};

const MostPopular = () => {

  const period = useTypedSelector(state => state.event.period);
  const chartsByPeriod = useTypedSelector(state => state.event.chartsByPeriod);

  const [chartName, setChartName] = useState<keyof EventsCharts>("recentAttendance");

  const chartData = useMemo<ChartItem[]>(() => {
    const data = chartsByPeriod[period];
    if (data && typeof data !== "string") {
      return data[chartName];
    }
    return [];
  }, [period, chartName, chartsByPeriod]);

  const barChartData = useMemo<any[][]>(() => {
    const result: any[][] = [];
    switch (chartName) {
      case "recentAttendance":
      case "highestAttendance":
        const headings = [
          "label",
          "total",
          { role: "style" },
          {
            sourceColumn: 0,
            role: "annotation",
            type: "string",
            calc: "stringify",
          },
        ];
        const totalItems = chartData.length;
        const data = chartData.map((item, i) => [item.label, item.value ?? 0, getColor(colors, i, totalItems), null]);
        result.push(headings, ...data);
        break;
    }
    return result;
  }, [chartName, chartData]);

  const comboBarChartData = useMemo<any[]>(() => {
    const result: (string | number)[][] = [];
    switch (chartName) {
      case "recentScores":
      case "highestScores":
        const headings = ["label", "Overall", "Content", "Presenter"];
        const data = chartData.map(item => [
          item.label,
          item.scores?.overall ?? 0,
          item.scores?.content ?? 0,
          item.scores?.presenter ?? 0,
        ]);
        result.push(headings, ...data);
        break;
    }
    return result;
  }, [chartName, chartData]);

  const comboBarChartOptions = useMemo(() => {
    switch (chartName) {
      case "recentScores":
      case "highestScores":
        return {
          ...baseComboBarChartOptions,
          colors: ["overall", "content", "presenter"].map((value, index) => getColor(colors, index, 3)),
        };
    }
  }, [chartName, chartData]);

  const pieChartData = useMemo<(string | number)[][]>(() => {
    const result: (string | number)[][] = [];
    switch (chartName) {
      case "attendanceByType":
        const data = chartData.map((item, i) => [item.label, item.value ?? 0]);
        result.push(["label", "value"], ...data);
        break;
    }
    return result;
  }, [chartName, chartData]);

  const pieChartColors = useMemo(() => {
    const arr: string[] = [];
    switch (chartName) {
      case "attendanceByType":
        const totalItems = chartData.length;
        return chartData.map((item, index) => getColor(colors, index, totalItems));
    }
    return arr;
  }, [chartName, chartData]);

  const pieChartOptions = useMemo(() => {
    switch (chartName) {
      case "attendanceByType":
        return {
          ...basePieChartOptions,
          slices: pieChartColors.map(color => ({ color })),
        };
    }
  }, [chartName, chartData, pieChartColors]);

  const isLoading = useMemo(() => chartsByPeriod[period] === "loading", [period, chartsByPeriod]);
  const isError = useMemo(() => chartsByPeriod[period] === "error", [period, chartsByPeriod]);

  const onChange = (e: SelectOnChangeEvent) => {
    setChartName(e.value as any);
  };

  return (
    <Col className="my-3 mostPopular">
      <Card>
        <Row>
          <Col md="12" lg="8" className="p-4">
            <CardTitle
              tag="h5"
              className="mt-3 text-primary text-center text-uppercase fs-9 fw-6"
            >
              MY EVENTS
            </CardTitle>

            <div className="position-relative" style={{ minHeight: "10rem" }}>
              {isLoading
                ? (<Loader show />)
                : isError
                  ? (<Error show />)
                  : chartData.length !== 0
                    ? (
                      <div className="reactgooglegraph-2">
                        {chartName === "recentScores"
                          ? (<Chart
                            key="recentScores"
                            chartType="ComboChart"
                            data={comboBarChartData}
                            options={comboBarChartOptions}
                          />)
                          : chartName === "highestScores"
                            ? (<Chart
                              key="highestScores"
                              chartType="ComboChart"
                              data={comboBarChartData}
                              options={comboBarChartOptions}
                            />)
                            : chartName === "recentAttendance"
                              ? (<Chart
                                key="recentAttendance"
                                chartType="ColumnChart"
                                data={barChartData}
                                options={barChartOptions}
                              />)
                              : chartName === "highestAttendance"
                                ? (<Chart
                                  key="highestAttendance"
                                  chartType="ColumnChart"
                                  data={barChartData}
                                  options={barChartOptions}
                                />)
                                : chartName === "attendanceByType"
                                  ? (<Chart
                                    key="attendanceByType"
                                    chartType="PieChart"
                                    data={pieChartData}
                                    options={pieChartOptions}
                                  />)
                                  : null}
                      </div>
                    )
                    : (
                      <p className="text-center">
                        <Info /> No data available
                      </p>
                    )}
            </div>
          </Col>
          <Col md="12" lg="4">
            <div className="p-2 mostPopular__sidebar">
              <SelectInput
                id="popular_categories"
                field="category"
                options={options}
                required={true}
                onChange={onChange}
                value={chartName}
              />
              <div className="pt-1 mostPopular__list">
                {chartData.length !== 0 && (
                  chartName === "recentAttendance" || chartName === "highestAttendance"
                    ? chartData.map((item, i, arr) => (
                      <ListGroupItem key={i}>
                        <div className="d-flex">
                          <div className="p-1">
                            <Badge
                              className="mostPopular__color"
                              style={{ backgroundColor: getColor(colors, i, arr.length) }}
                            >
                              {" "}
                            </Badge>
                          </div>
                          <div className="p-1">
                            {item.label}
                            {item.date && (
                              <div>
                                <small>
                                  {format(new Date(item.date), "dd/MM/yyyy")}
                                </small>
                              </div>
                            )}
                          </div>
                          <div className="ml-auto p-1 font-weight-bold">{item.value?.toLocaleString()}</div>
                        </div>
                      </ListGroupItem>
                    ))
                    : chartName === "recentScores" || chartName === "highestScores"
                      ? chartData.map((item, i, arr) => (
                        <ListGroupItem key={i}>
                          <div className="d-flex">
                            <div className="p-1">
                              {item.label}
                              {item.date && (
                                <div>
                                  <small>
                                    {format(new Date(item.date), "dd/MM/yyyy")}
                                  </small>
                                </div>
                              )}
                            </div>
                            <div className="ml-auto p-1 font-weight-bold">{item.value?.toLocaleString()}</div>
                          </div>
                        </ListGroupItem>
                      ))
                      : chartName === "attendanceByType"
                        ? chartData.map((item, i, arr) => (
                          <ListGroupItem key={i}>
                            <div className="d-flex">
                              <div className="p-1">
                                <Badge
                                  className="mostPopular__color"
                                  style={{ backgroundColor: getColor(colors, i, arr.length) }}
                                >
                                  {" "}
                                </Badge>
                              </div>
                              <div className="p-1">
                                {item.label}
                              </div>
                              <div className="ml-auto p-1 font-weight-bold">{item.value?.toLocaleString()}</div>
                            </div>
                          </ListGroupItem>
                        ))
                        : null
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </Col>
  );
};

export default MostPopular;
